/**
 * Public Sentiment <-> site evidence (SED) readiness — ONE contract shared by
 * the enqueue route and the client dispatch preflight.
 *
 * WHY. Public Sentiment is the one analysis whose evidence is refreshed
 * SYNCHRONOUSLY at enqueue (`refreshPublicSentimentServer` publishes a new
 * immutable SED before the job exists), so it cannot wait out a site-evidence
 * build the way the report worker does for Feasibility / Environmental. Live on
 * Holly Ridge: Feasibility's coordinator started an SED top-up ~20 s after the
 * user launched Feasibility + Environmental + Public Sentiment together; the
 * Sentiment POST landed mid-build, the refresh threw "Rebuild current site
 * evidence…", the route answered 500 and the UI showed "Generation Failed" and
 * a red "Failed" Analyses row for a condition that fixes itself in minutes.
 *
 * The condition is EXPECTED, not a failure:
 *   - `sed-rebuilding`       an evidence build is running. Transient; the client
 *                            holds the run and dispatches it when the build
 *                            finishes (in this page's memory: the page must
 *                            stay open). If its automatic wait or retries run
 *                            out, it records a hold that says Public Sentiment
 *                            did not start (Run again).
 *   - `sed-rebuild-required` the current evidence is not usable as a source
 *                            (failed / missing build, dirty sections, a stored
 *                            SED that predates the current intent, documents or
 *                            storage layout). Needs a rebuild; never auto-runs.
 *   - `sed-evidence-moved`   the site's SED pointer moved to a newer version
 *                            while the sentiment scan ran (a build or another
 *                            refresh published first), so the scan was not
 *                            committed. Transient, like `sed-rebuilding`: the
 *                            client retries it within the same budgets.
 *
 * Pure and client-safe: no firebase / firebase-admin imports.
 */
import type { SiteEntity } from '../../types/site';

/** Machine code the enqueue route answers (HTTP 409) for this condition. */
export const SENTIMENT_EVIDENCE_NOT_READY_CODE = 'sentiment-evidence-not-ready' as const;

export type SentimentEvidenceNotReadyReason = 'sed-rebuilding' | 'sed-rebuild-required' | 'sed-evidence-moved';

/** Waiting (not user action) resolves every reason except `sed-rebuild-required`. */
export function isRetryableSentimentEvidenceReason(reason: SentimentEvidenceNotReadyReason): boolean {
  return reason !== 'sed-rebuild-required';
}

/**
 * User-facing copy, one per reason, for the route's 409 body. It states only
 * what the server observed: it does not guess who started a build (the user's
 * own Rebuild SED or an upload can start one too) or how long it will take.
 */
export const SENTIMENT_EVIDENCE_NOT_READY_MESSAGES: Readonly<Record<SentimentEvidenceNotReadyReason, string>> = {
  'sed-rebuilding':
    'Site evidence is refreshing. Public Sentiment needs the finished evidence and can run once the refresh completes.',
  'sed-rebuild-required':
    'Site evidence has changes that need a rebuild before Public Sentiment can run. Rebuild site evidence, then run it again.',
  'sed-evidence-moved':
    'Site evidence changed while Public Sentiment was collecting, so it did not start. It can run again on the new evidence.',
};

/**
 * Inline notice while a Public Sentiment run is held for an in-progress build.
 * The wait lives only in this page's memory (the store's evidence watcher), so
 * the copy says the page must stay open; the store also warns before unload.
 */
export const SENTIMENT_EVIDENCE_WAITING_NOTICE =
  'Waiting for site evidence to finish refreshing — Public Sentiment will start automatically while this page stays open.';

/** beforeunload text while a Public Sentiment run waits (browsers may show their own). */
export const SENTIMENT_EVIDENCE_LEAVE_WARNING =
  'Public Sentiment is waiting for site evidence and has not started. Leaving this page cancels it.';

/**
 * Why a held (never dispatched) Public Sentiment run stopped, as shown to the
 * user. Chosen from the observed state when the hold is recorded
 * (`sentimentEvidenceHoldMessage`), never from the reason alone.
 */
export type SentimentEvidenceHoldCause =
  | 'rebuild-required'
  | 'build-incomplete'
  | 'not-built'
  | 'kept-changing'
  | 'evidence-moved'
  | 'wait-timeout';

export const SENTIMENT_EVIDENCE_HOLD_MESSAGES: Readonly<Record<SentimentEvidenceHoldCause, string>> = {
  // Dirty sections, or a server-only refusal (stale intent, documents or storage path).
  'rebuild-required': SENTIMENT_EVIDENCE_NOT_READY_MESSAGES['sed-rebuild-required'],
  'build-incomplete':
    'The last site-evidence build did not complete. Rebuild site evidence, then run Public Sentiment again.',
  'not-built':
    'Site evidence has not been built for this site yet. Build site evidence, then run Public Sentiment.',
  'kept-changing':
    'Public Sentiment did not start because site evidence kept changing while it tried to run. Run it again.',
  'evidence-moved':
    'Site evidence changed while Public Sentiment was collecting — run it again.',
  'wait-timeout':
    'Public Sentiment did not start because site evidence was still refreshing after an hour. Run it again once the refresh finishes.',
};

/**
 * `progressStage` a held (waiting) run carries in `activeGenerations`. It
 * deliberately starts with "Queued" so every runtime projection that already
 * reads a 0% "queue" stage as `queued` (deriveReportRuntimeByType) shows the run
 * as queued — never failed — with no per-surface plumbing. Surfaces that render
 * progress detect it with `isSentimentEvidenceWaitStage` (the same pattern
 * BootstrapWaitBanner uses for the bootstrap-wait copy).
 */
export const SENTIMENT_EVIDENCE_WAIT_STAGE = 'Queued — waiting for site evidence to finish refreshing';

export function isSentimentEvidenceWaitStage(stage: string | null | undefined): boolean {
  return stage === SENTIMENT_EVIDENCE_WAIT_STAGE;
}

export function isSentimentEvidenceNotReadyReason(value: unknown): value is SentimentEvidenceNotReadyReason {
  return value === 'sed-rebuilding' || value === 'sed-rebuild-required' || value === 'sed-evidence-moved';
}

/**
 * Thrown by `refreshPublicSentimentServer` when the site's evidence cannot be a
 * sentiment source right now. `message` is the pre-existing (operator-facing)
 * text, UNCHANGED, so callers that only read `message` (the intake CLI, the
 * invalidation reaper) behave exactly as before. There is intentionally no
 * `code` property: the reaper and the intake CLI branch on `error.code`, and
 * this condition must not change their classification.
 */
export class SentimentEvidenceNotReadyError extends Error {
  readonly sentimentEvidenceReason: SentimentEvidenceNotReadyReason;
  /** True when waiting (not user action) resolves it. */
  readonly retryable: boolean;
  /**
   * True when the refusal came AFTER the full sentiment scan ran (the site's
   * evidence changed mid-scan). Such a refusal is expensive — the scan ran and
   * an unreferenced immutable SED object may already have been uploaded — so
   * the client allows it at most one automatic retry.
   */
  readonly afterScan: boolean;

  constructor(message: string, reason: SentimentEvidenceNotReadyReason, options: { afterScan?: boolean } = {}) {
    super(message);
    this.name = 'SentimentEvidenceNotReadyError';
    this.sentimentEvidenceReason = reason;
    this.retryable = isRetryableSentimentEvidenceReason(reason);
    this.afterScan = options.afterScan === true;
  }
}

/** `instanceof`, with a structural fallback for a duplicated module instance. */
export function isSentimentEvidenceNotReadyError(error: unknown): error is SentimentEvidenceNotReadyError {
  if (error instanceof SentimentEvidenceNotReadyError) return true;
  return error instanceof Error
    && error.name === 'SentimentEvidenceNotReadyError'
    && isSentimentEvidenceNotReadyReason((error as { sentimentEvidenceReason?: unknown }).sentimentEvidenceReason);
}

export interface SentimentEvidenceNotReadyResponseBody {
  error: string;
  code: typeof SENTIMENT_EVIDENCE_NOT_READY_CODE;
  reason: SentimentEvidenceNotReadyReason;
  retryable: boolean;
  /** Present (true) only when the refusal followed a completed sentiment scan. */
  afterScan?: true;
}

export function sentimentEvidenceNotReadyResponseBody(
  reason: SentimentEvidenceNotReadyReason,
  options: { afterScan?: boolean } = {},
): SentimentEvidenceNotReadyResponseBody {
  return {
    error: SENTIMENT_EVIDENCE_NOT_READY_MESSAGES[reason],
    code: SENTIMENT_EVIDENCE_NOT_READY_CODE,
    reason,
    retryable: isRetryableSentimentEvidenceReason(reason),
    ...(options.afterScan ? { afterScan: true as const } : {}),
  };
}

/**
 * HTTP mapping for route catch blocks: a 409 with the friendly message instead
 * of the generic 500. `null` when `error` is anything else.
 */
export function sentimentEvidenceNotReadyHttpResponse(
  error: unknown,
): { status: 409; body: SentimentEvidenceNotReadyResponseBody } | null {
  if (!isSentimentEvidenceNotReadyError(error)) return null;
  return {
    status: 409,
    body: sentimentEvidenceNotReadyResponseBody(error.sentimentEvidenceReason, {
      afterScan: (error as { afterScan?: unknown }).afterScan === true,
    }),
  };
}

export interface ParsedSentimentEvidenceNotReady {
  reason: SentimentEvidenceNotReadyReason;
  retryable: boolean;
  /** The refusal followed a completed (expensive) sentiment scan. */
  afterScan: boolean;
  message: string;
}

/** Client side: recognise the route's 409. `null` for every other response. */
export function parseSentimentEvidenceNotReadyResponse(
  status: number,
  body: unknown,
): ParsedSentimentEvidenceNotReady | null {
  if (status !== 409 || !body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (record.code !== SENTIMENT_EVIDENCE_NOT_READY_CODE) return null;
  if (!isSentimentEvidenceNotReadyReason(record.reason)) return null;
  const reason = record.reason;
  return {
    reason,
    // The reason decides retryability; a malformed flag cannot turn a
    // rebuild-required hold into an auto-dispatch loop.
    retryable: isRetryableSentimentEvidenceReason(reason),
    afterScan: record.afterScan === true,
    message: SENTIMENT_EVIDENCE_NOT_READY_MESSAGES[reason],
  };
}

// ── Site-document classification ─────────────────────────────────────────

export type SentimentSiteEvidenceFields = Pick<
  SiteEntity,
  | 'sedStatus'
  | 'siteEvidenceDirtySections'
  | 'sedBootstrapLockStatus'
  | 'sedBootstrapLockedAt'
>;

/**
 * Mirrors `LOCK_TTL_MS` in `src/server/sedBootstrap/executeSedBootstrapJob.ts`
 * (a server module this client-safe file cannot import). A test pins equality.
 */
export const SITE_EVIDENCE_BUILD_LOCK_TTL_MS = 90_000;

/**
 * True while an evidence build is running: `sedStatus: 'bootstrapping'` (set by
 * the build's lock acquisition and by the client's optimistic retry stamp), or
 * a bootstrap lock whose heartbeat is inside the lease. A lock with no readable
 * heartbeat is NOT treated as a running build here — waiting on it could never
 * end, and `sedStatus` still reports a genuine build.
 */
export function siteEvidenceBuildInProgress(
  site: SentimentSiteEvidenceFields,
  nowMs: number = Date.now(),
): boolean {
  if (site.sedStatus === 'bootstrapping') return true;
  if (site.sedBootstrapLockStatus !== 'locked') return false;
  const lockedAt = Date.parse(site.sedBootstrapLockedAt ?? '');
  return Number.isFinite(lockedAt) && nowMs - lockedAt < SITE_EVIDENCE_BUILD_LOCK_TTL_MS;
}

/**
 * Reason for a site that ALREADY failed the server precondition. It never
 * decides WHETHER the refresh is refused — only which kind of refusal it is.
 */
export function sentimentEvidenceNotReadyReasonForSite(
  site: SentimentSiteEvidenceFields,
  nowMs: number = Date.now(),
): SentimentEvidenceNotReadyReason {
  return siteEvidenceBuildInProgress(site, nowMs) ? 'sed-rebuilding' : 'sed-rebuild-required';
}

export type SentimentSitePreflight =
  | { ready: true }
  | { ready: false; reason: SentimentEvidenceNotReadyReason };

/**
 * Client preflight on the LIVE site document. Mirrors the server's site-doc
 * precondition (complete status, no dirty sections) and additionally waits on a
 * running build even when the status still reads complete. It cannot see the
 * owner-scoped storage path or the stored SED's intent/document hashes; the
 * server's 409 covers those (race safety).
 */
export function classifySentimentSiteEvidence(
  site: SentimentSiteEvidenceFields,
  nowMs: number = Date.now(),
): SentimentSitePreflight {
  if (siteEvidenceBuildInProgress(site, nowMs)) return { ready: false, reason: 'sed-rebuilding' };
  if (site.sedStatus !== 'complete' || (site.siteEvidenceDirtySections?.length ?? 0) > 0) {
    return { ready: false, reason: 'sed-rebuild-required' };
  }
  return { ready: true };
}

// ── Held-run scheduling (client) ─────────────────────────────────────────

/** Dispatches a held run may make after retryable 409s before it stops auto-running. */
export const SENTIMENT_EVIDENCE_MAX_AUTO_DISPATCHES = 3;
/**
 * Automatic retries allowed after a refusal that followed a completed
 * sentiment scan (`afterScan`). Each such retry repeats the full scan, and the
 * refused attempt may have left an unreferenced SED object in storage, so one.
 */
export const SENTIMENT_EVIDENCE_MAX_AFTER_SCAN_RETRIES = 1;
/** Minimum gap before re-dispatching after a retryable 409 (the live doc can lag the server). */
export const SENTIMENT_EVIDENCE_RETRY_DELAY_MS = 20_000;
/** Ceiling on one wait. The stuck-build watchdogs fail a dead build well before this. */
export const SENTIMENT_EVIDENCE_MAX_WAIT_MS = 60 * 60_000;

/** Why an automatic wait/retry budget ran out (the hold then says "did not start — run it again"). */
export type SentimentEvidenceExhaustion = 'retries' | 'wait';

export type SentimentWaitStep =
  | { step: 'dispatch' }
  | { step: 'wait'; recheckInMs?: number }
  | { step: 'hold'; reason: SentimentEvidenceNotReadyReason; exhausted?: SentimentEvidenceExhaustion }
  | { step: 'drop' };

/**
 * Next step for a held Public Sentiment run, from the live site document.
 * `site` undefined means the site left the store (deleted / signed out): the
 * run is dropped without an outcome. The 60-minute ceiling only ends a wait
 * while the evidence is still NOT ready: evidence that is ready dispatches
 * (after any retry delay) even when the ceiling has just passed.
 */
export function nextSentimentWaitStep(input: {
  site: SentimentSiteEvidenceFields | undefined;
  nowMs: number;
  waitStartedAtMs: number;
  notBeforeMs?: number;
}): SentimentWaitStep {
  if (!input.site) return { step: 'drop' };
  const preflight = classifySentimentSiteEvidence(input.site, input.nowMs);
  if (!preflight.ready) {
    if (preflight.reason === 'sed-rebuild-required') return { step: 'hold', reason: 'sed-rebuild-required' };
    if (input.nowMs - input.waitStartedAtMs >= SENTIMENT_EVIDENCE_MAX_WAIT_MS) {
      return { step: 'hold', reason: 'sed-rebuilding', exhausted: 'wait' };
    }
    return { step: 'wait' };
  }
  const notBefore = input.notBeforeMs ?? 0;
  if (input.nowMs < notBefore) return { step: 'wait', recheckInMs: notBefore - input.nowMs };
  return { step: 'dispatch' };
}

// ── Held (not dispatched) runs ───────────────────────────────────────────

/**
 * A Public Sentiment run the client did NOT dispatch because the evidence
 * needs a rebuild, or because its automatic wait/retry budget ran out.
 * Never a generation outcome, so no surface can render it as "Failed".
 */
export interface SentimentEvidenceHold {
  /** The held run id (the wizard / caller clientRunId). */
  id: string;
  siteId: string;
  reason: SentimentEvidenceNotReadyReason;
  message: string;
  heldAt: string;
  /** SED pointer when the run was held; a later build supersedes the hold. */
  sedId?: string;
  sedVersion?: number;
  /**
   * Set when the automatic retries ('retries') or the wait ceiling ('wait')
   * ran out. Such a hold says Public Sentiment did NOT start and stays until
   * the user dismisses it or starts a new attempt, whatever the evidence
   * readiness — otherwise it would vanish the moment evidence reads ready,
   * which is exactly when it is recorded after exhausted retries.
   */
  exhausted?: SentimentEvidenceExhaustion;
}

export type SentimentHoldSiteFields = SentimentSiteEvidenceFields & Pick<SiteEntity, 'sedId' | 'sedVersion'>;

/**
 * Hold copy from the OBSERVED state, not the reason alone: a failed or partial
 * build did not complete (it has no "changes"), a never-built site has no
 * evidence yet, and only dirty sections or a server-only refusal (stale
 * intent, documents or storage path — the site doc reads complete) are
 * "changes that need a rebuild".
 */
export function sentimentEvidenceHoldCause(input: {
  reason: SentimentEvidenceNotReadyReason;
  exhausted?: SentimentEvidenceExhaustion;
  site: Pick<SiteEntity, 'sedStatus' | 'sedId' | 'siteEvidenceDirtySections'> | undefined;
}): SentimentEvidenceHoldCause {
  if (input.exhausted === 'wait') return 'wait-timeout';
  if (input.reason === 'sed-evidence-moved') return 'evidence-moved';
  if (input.exhausted === 'retries' || input.reason === 'sed-rebuilding') return 'kept-changing';
  const site = input.site;
  if (!site || (site.siteEvidenceDirtySections?.length ?? 0) > 0 || site.sedStatus === 'complete') return 'rebuild-required';
  if (site.sedStatus === 'failed' || site.sedStatus === 'partial') return 'build-incomplete';
  if (!site.sedStatus && !site.sedId) return 'not-built';
  return 'rebuild-required';
}

export function sentimentEvidenceHoldMessage(input: Parameters<typeof sentimentEvidenceHoldCause>[0]): string {
  return SENTIMENT_EVIDENCE_HOLD_MESSAGES[sentimentEvidenceHoldCause(input)];
}

/**
 * Whether a hold still describes the live site. An exhausted hold stands until
 * dismissed or superseded by a new attempt (the store removes it then). Any
 * other hold stands while evidence is not ready; once ready, a
 * `sed-rebuild-required` hold is over when a build moved the SED pointer (the
 * server-only refusals — stale intent, documents or storage path — are only
 * cleared by a rebuild).
 */
export function sentimentEvidenceHoldIsCurrent(
  hold: SentimentEvidenceHold,
  site: SentimentHoldSiteFields | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!site) return false;
  if (hold.exhausted) return true;
  if (!classifySentimentSiteEvidence(site, nowMs).ready) return true;
  if (isRetryableSentimentEvidenceReason(hold.reason)) return false;
  return site.sedId === hold.sedId && site.sedVersion === hold.sedVersion;
}

/** Newest hold recorded for `siteId` (at most one is kept per site). */
export function latestSentimentEvidenceHoldForSite(
  holds: Readonly<Record<string, SentimentEvidenceHold>> | undefined,
  siteId: string,
): SentimentEvidenceHold | null {
  let latest: SentimentEvidenceHold | null = null;
  for (const hold of Object.values(holds ?? {})) {
    if (hold.siteId !== siteId) continue;
    if (!latest || Date.parse(hold.heldAt) >= Date.parse(latest.heldAt)) latest = hold;
  }
  return latest;
}

// ── Caller-side disposition ──────────────────────────────────────────────

/**
 * What the store did with a Public Sentiment request that resolved without a
 * job: `waiting` (held for a running build; it dispatches automatically while
 * the page stays open) or `held` (not dispatched; a hold explains why). `null`
 * for everything else — including every other report type.
 *
 * Callers that show "started" / "failed" feedback after `generateTypedReport`
 * resolves (rerun panel, fast-path rerun) use this so a held run is neither.
 * With `runId` the check is exact; without it (callers that let the store mint
 * the run id) it is site-scoped, which is exact too because a new Public
 * Sentiment attempt supersedes every earlier wait and hold for its site.
 */
export type PublicSentimentEvidenceDisposition = 'waiting' | 'held' | null;

export function publicSentimentEvidenceDisposition(
  siteRag: {
    activeGenerations?: Readonly<Record<string, { siteId: string; waitingForSiteEvidence?: boolean }>>;
    sentimentEvidenceHolds?: Readonly<Record<string, SentimentEvidenceHold>>;
  } | undefined,
  target: { siteId: string; reportTypeId: string | undefined; runId?: string },
): PublicSentimentEvidenceDisposition {
  if (target.reportTypeId !== 'public-sentiment' || !siteRag) return null;
  const runs = siteRag.activeGenerations ?? {};
  const holds = siteRag.sentimentEvidenceHolds ?? {};
  if (target.runId) {
    if (runs[target.runId]?.waitingForSiteEvidence) return 'waiting';
    return holds[target.runId] ? 'held' : null;
  }
  if (Object.values(runs).some(run => run.siteId === target.siteId && run.waitingForSiteEvidence)) return 'waiting';
  return Object.values(holds).some(hold => hold.siteId === target.siteId) ? 'held' : null;
}
